import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {AlertTriangle, Loader2} from 'lucide-react';
import {toast} from 'react-toastify';
import AdminService from '@/modules/admin/services/adminService';
import type {AdminUserMergeCandidate} from '@/shared/types/api';
import {formatBirthdate} from '@/shared/utils/formatBirthdate';

interface Props {
    open: boolean;
    userIds: number[];
    onClose: () => void;
    /** Called after a successful merge so the parent can refresh its search results. */
    onMerged?: (survivorId: number) => void;
}

const CONFIRM_WORD = 'MERGE';

/**
 * Recommend the most "real" account as the survivor: a row with a login wins;
 * otherwise the one with the most records / most recent activity. Tombstones
 * (already merged away) are never recommended.
 */
function pickRecommendedSurvivor(candidates: AdminUserMergeCandidate[]): number | null {
    const eligible = candidates.filter((c) => c.mergedIntoUserId == null);
    if (eligible.length === 0) return null;
    const score = (c: AdminUserMergeCandidate) =>
        (c.hasPassword ? 1_000_000_000 : 0) +
        (c.verified ? 100_000_000 : 0) +
        c.recordCount * 1000 +
        (c.lastActivityAt ? Math.min(999, Math.floor(Date.parse(c.lastActivityAt) / 1e11)) : 0);
    return [...eligible].sort((a, b) => score(b) - score(a))[0].id;
}

function fullName(c: AdminUserMergeCandidate): string {
    return [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unknown';
}

const SignalBadge: React.FC<{tone: 'good' | 'warn' | 'neutral'; children: React.ReactNode}> = ({tone, children}) => {
    const cls =
        tone === 'good'
            ? 'bg-mld-primary/10 text-mld-primary border-mld-primary/30'
            : tone === 'warn'
              ? 'bg-mld-tertiary/10 text-mld-tertiary border-mld-tertiary/30'
              : 'bg-[var(--mld-background)] text-mld-text-secondary border-mld-panel-border';
    return (
        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}>{children}</span>
    );
};

export const AdminUserMergeModal: React.FC<Props> = ({open, userIds, onClose, onMerged}) => {
    const queryClient = useQueryClient();
    const [survivorId, setSurvivorId] = useState<number | null>(null);
    const [confirmText, setConfirmText] = useState('');

    const candidatesQuery = useQuery({
        queryKey: ['admin-merge-candidates', userIds],
        enabled: open && userIds.length >= 2,
        queryFn: () => AdminService.getMergeCandidates(userIds),
    });

    const candidates = useMemo(() => candidatesQuery.data ?? [], [candidatesQuery.data]);
    const recommendedId = useMemo(() => pickRecommendedSurvivor(candidates), [candidates]);

    // Default the survivor to the recommendation once candidates load; reset on close.
    useEffect(() => {
        if (!open) {
            setSurvivorId(null);
            setConfirmText('');
            return;
        }
        if (survivorId == null && recommendedId != null) {
            setSurvivorId(recommendedId);
        }
    }, [open, recommendedId, survivorId]);

    const loserIds = useMemo(
        () => candidates.map((c) => c.id).filter((id) => id !== survivorId),
        [candidates, survivorId]
    );

    const previewQuery = useQuery({
        queryKey: ['admin-merge-preview', survivorId, loserIds],
        enabled: open && survivorId != null && loserIds.length > 0,
        queryFn: () => AdminService.previewMergeUsers({survivorId: survivorId!, loserIds}),
    });

    const mergeMutation = useMutation({
        mutationFn: () => AdminService.mergeUsers({survivorId: survivorId!, loserIds}),
        onSuccess: (result) => {
            toast.success(`Merged ${loserIds.length + 1} records into #${result.survivorId}.`);
            queryClient.invalidateQueries({queryKey: ['admin-patient-search']});
            onMerged?.(result.survivorId);
            onClose();
        },
        onError: (err: unknown) => {
            const message =
                (err as {data?: {error?: string}})?.data?.error ??
                (err as Error)?.message ??
                'Failed to merge records.';
            toast.error(message);
        },
    });

    const preview = previewQuery.data;
    const confirmReady =
        survivorId != null && loserIds.length > 0 && Boolean(preview) && confirmText.trim().toUpperCase() === CONFIRM_WORD;

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-2xl rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                                <div className="border-b border-mld-panel-border px-6 py-4">
                                    <Dialog.Title className="text-lg font-semibold text-mld-menu-text">
                                        Merge patient records
                                    </Dialog.Title>
                                    <p className="text-xs text-mld-muted-text">
                                        Pick the record to keep. Everything else (orders, results, payments, profile
                                        data) moves onto it; the others are retired.
                                    </p>
                                </div>

                                <div className="max-h-[60vh] space-y-5 overflow-y-auto px-6 py-5">
                                    {candidatesQuery.isLoading ? (
                                        <div className="flex items-center gap-2 text-sm text-mld-muted-text">
                                            <Loader2 className="h-4 w-4 animate-spin" /> Loading records…
                                        </div>
                                    ) : candidatesQuery.isError ? (
                                        <div className="text-sm text-mld-tertiary">Failed to load records.</div>
                                    ) : (
                                        <>
                                            <div className="space-y-2">
                                                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-mld-muted-text">
                                                    Keep this record (survivor)
                                                </div>
                                                {candidates.map((c) => {
                                                    const isTombstone = c.mergedIntoUserId != null;
                                                    const selected = c.id === survivorId;
                                                    return (
                                                        <label
                                                            key={c.id}
                                                            className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 ${
                                                                selected
                                                                    ? 'border-mld-primary bg-mld-primary/5'
                                                                    : 'border-mld-panel-border hover:bg-[var(--mld-background)]'
                                                            } ${isTombstone ? 'opacity-60' : ''}`}>
                                                            <input
                                                                type="radio"
                                                                name="survivor"
                                                                className="mt-1 accent-mld-primary"
                                                                checked={selected}
                                                                disabled={isTombstone}
                                                                onChange={() => setSurvivorId(c.id)}
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="font-semibold text-mld-menu-text">
                                                                        {fullName(c)}
                                                                    </span>
                                                                    <span className="text-xs text-mld-muted-text">
                                                                        #{c.id} · DOB {formatBirthdate(c.birthday)}
                                                                    </span>
                                                                    {c.id === recommendedId ? (
                                                                        <SignalBadge tone="good">Recommended</SignalBadge>
                                                                    ) : null}
                                                                </div>
                                                                <div className="mt-0.5 truncate text-xs text-mld-muted-text">
                                                                    {c.email}
                                                                </div>
                                                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                                    {c.hasPassword ? (
                                                                        <SignalBadge tone="good">Has login</SignalBadge>
                                                                    ) : (
                                                                        <SignalBadge tone="warn">Stub / no login</SignalBadge>
                                                                    )}
                                                                    {c.verified ? (
                                                                        <SignalBadge tone="neutral">Verified</SignalBadge>
                                                                    ) : null}
                                                                    <SignalBadge tone="neutral">
                                                                        {c.recordCount} record
                                                                        {c.recordCount === 1 ? '' : 's'}
                                                                    </SignalBadge>
                                                                    {c.lastActivityAt ? (
                                                                        <SignalBadge tone="neutral">
                                                                            Active {formatBirthdate(c.lastActivityAt)}
                                                                        </SignalBadge>
                                                                    ) : null}
                                                                    {isTombstone ? (
                                                                        <SignalBadge tone="warn">
                                                                            Already merged
                                                                        </SignalBadge>
                                                                    ) : null}
                                                                </div>
                                                                {c.organizations.length > 0 ? (
                                                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                                                        {c.organizations.map((org) => (
                                                                            <span
                                                                                key={org.id}
                                                                                className="rounded-full border border-mld-panel-border bg-[var(--mld-background)] px-2 py-0.5 text-[11px] text-mld-text-secondary">
                                                                                {org.displayName || org.name}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>

                                            {/* Live impact for the chosen survivor */}
                                            <div className="rounded-lg border border-mld-panel-border bg-[var(--mld-background)] px-4 py-3">
                                                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-mld-muted-text">
                                                    What will happen
                                                </div>
                                                {previewQuery.isFetching ? (
                                                    <div className="mt-2 flex items-center gap-2 text-sm text-mld-muted-text">
                                                        <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
                                                    </div>
                                                ) : preview ? (
                                                    <div className="mt-2 space-y-2 text-sm text-mld-menu-text">
                                                        <div>
                                                            <span className="font-semibold">{preview.totalMoved}</span>{' '}
                                                            record{preview.totalMoved === 1 ? '' : 's'} move to{' '}
                                                            <span className="font-semibold">
                                                                {fullName(preview.survivor)} (#{preview.survivor.id})
                                                            </span>
                                                            {preview.totalDropped > 0 ? (
                                                                <>
                                                                    {' '}
                                                                    · <span className="font-semibold">
                                                                        {preview.totalDropped}
                                                                    </span>{' '}
                                                                    duplicate
                                                                    {preview.totalDropped === 1 ? '' : 's'} removed
                                                                </>
                                                            ) : null}
                                                        </div>
                                                        {preview.impact.length > 0 ? (
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {preview.impact.map((row) => (
                                                                    <span
                                                                        key={row.table}
                                                                        className="rounded border border-mld-panel-border bg-white px-2 py-0.5 font-mono text-[11px] text-mld-text-secondary">
                                                                        {row.table}: +{row.moved}
                                                                        {row.dropped > 0 ? ` / −${row.dropped}` : ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-mld-muted-text">
                                                                The other record{loserIds.length === 1 ? '' : 's'} have
                                                                no attached data — only the account row is retired.
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : previewQuery.isError ? (
                                                    <div className="mt-2 text-sm text-mld-tertiary">
                                                        Couldn’t calculate impact. Try a different survivor.
                                                    </div>
                                                ) : null}
                                            </div>

                                            {/* Irreversible warning + typed confirmation */}
                                            <div className="flex items-start gap-2 rounded-lg border border-mld-tertiary/30 bg-mld-tertiary/5 px-4 py-3 text-sm text-mld-menu-text">
                                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-mld-tertiary" />
                                                <div>
                                                    This can’t be undone from the UI. The retired records are kept as
                                                    tombstones for audit, but their data now belongs to the survivor.
                                                    Type <span className="font-mono font-semibold">{CONFIRM_WORD}</span>{' '}
                                                    to confirm.
                                                </div>
                                            </div>
                                            <input
                                                type="text"
                                                value={confirmText}
                                                onChange={(e) => setConfirmText(e.target.value)}
                                                placeholder={CONFIRM_WORD}
                                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-mld-menu-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-mld-primary"
                                            />
                                        </>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 border-t border-mld-panel-border px-6 py-4">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={mergeMutation.isPending}
                                        className="rounded-md border border-mld-panel-border px-4 py-2 text-sm font-semibold text-mld-text-secondary hover:bg-[var(--mld-background)] disabled:opacity-50">
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => mergeMutation.mutate()}
                                        disabled={!confirmReady || mergeMutation.isPending}
                                        className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
                                        {mergeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                        Merge records
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default AdminUserMergeModal;
